import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';

import { PermissionService } from '../../../core/services/permission.service';
import { PERMISSIONS } from '../../../core/constants/permissions.constant';

interface MenuItem {
  label: string;
  icon: string;
  route: string;
  children?: MenuItem[];
  expanded?: boolean;
  permissions?: string[];
}

@Component({
  selector: 'app-side-nav',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './side-nav.component.html',
  styleUrls: ['./side-nav.component.scss']
})
export class SideNavComponent implements OnInit {

  isExpanded = false;
  expandedItems: { [key: string]: boolean } = {};

  private allMenuItems: MenuItem[] = [
    {
      label: 'Home',
      icon: 'fas fa-home',
      route: '/dashboard'
    },
    {
      label: 'Contacts',
      icon: 'fas fa-users',
      route: '/contacts',
      permissions: [PERMISSIONS.CONTACTS_LIST]
    },
    {
      label: 'Companies',
      icon: 'fas fa-building',
      route: '/companies',
      permissions: [PERMISSIONS.COMPANIES_LIST]
    },
    {
      label: 'Projects',
      icon: 'fas fa-project-diagram',
      route: '/projects',
      permissions: [PERMISSIONS.PROJECTS_LIST]
    },
    {
      label: 'Pipeline',
      icon: 'fas fa-filter',
      route: '/pipeline',
      children: [
        {
          label: 'BD Lists',
          icon: 'fas fa-list',
          route: '/bd-lists',
          permissions: [PERMISSIONS.PROJECTS_BD_LIST_ACCESS]
        },
        {
          label: 'Pre-Pipeline',
          icon: 'fas fa-tasks',
          route: '/pre-pipeline',
          permissions: [PERMISSIONS.PRE_PIPELINE_VIEW]
        },
        {
          label: 'Pipeline',
          icon: 'fas fa-stream',
          route: '/pipeline',
          permissions: [PERMISSIONS.PIPELINE_VIEW]
        }
      ],
    },
    {
      label: 'Settings',
      icon: 'fas fa-cog',
      route: '/project-settings',
      children: [
        {
          label: 'Admin Users',
          icon: 'fas fa-user',
          route: '/project-settings/users',
          permissions: [PERMISSIONS.SETTINGS_ADMIN_USERS_LIST]
        },
        {
          label: 'Admin Roles',
          icon: 'fas fa-user-shield',
          route: '/project-settings/roles',
          permissions: [PERMISSIONS.SETTINGS_ROLES_LIST]
        },
        {
          label: 'Clients',
          icon: 'fas fa-person',
          route: '/project-settings/clients',
          permissions: [PERMISSIONS.SETTINGS_CLIENT_USERS_LIST]
        },
        {
          label: 'Lists',
          icon: 'fas fa-bell',
          route: '/project-settings/lists',
          permissions: [PERMISSIONS.SETTINGS_LISTS_LIST]
        },
        {
          label: 'Form Links',
          icon: 'fas fa-shield-alt',
          route: '/project-settings/form-links',
          permissions: [PERMISSIONS.SETTINGS_FORM_LINKS_VIEW]
        },
        {
          label: 'Email Templates',
          icon: 'fas fa-plug',
          route: '/project-settings/email-templates',
          permissions: [PERMISSIONS.SETTINGS_EMAIL_TEMPLATES_VIEW]
        },
      ]
    },
    /*{
      label: 'Profile',
      icon: 'fas fa-user',
      route: '/my-commissions'
    }*/
  ];

  menuItems: MenuItem[] = [];

  constructor(private permissionService: PermissionService) { }

  ngOnInit(): void {
    this.filterMenuItems();
  }

  private filterMenuItems(): void {
    this.menuItems = this.allMenuItems.map(item => this.filterMenuItem(item)).filter(item => item !== null) as MenuItem[];
  }

  private filterMenuItem(item: MenuItem): MenuItem | null {
    if (item.permissions && item.permissions.length > 0) {
      const hasPermission = item.permissions.some(permission =>
        this.permissionService.hasPermission(permission)
      );

      if (!hasPermission) {
        return null;
      }
    }

    if (item.children && item.children.length > 0) {
      const filteredChildren = item.children
        .map(child => this.filterMenuItem(child))
        .filter(child => child !== null) as MenuItem[];

      if (filteredChildren.length === 0) {
        return null;
      }

      return {
        ...item,
        children: filteredChildren
      };
    }

    return item;

  }

  toggleSidebar(): void {
    this.isExpanded = !this.isExpanded;
  }

  toggleSubmenu(item: MenuItem): void {
    if (item.children) {
      const key = item.label;
      this.expandedItems[key] = !this.expandedItems[key];
    }
  }

  isSubmenuExpanded(item: MenuItem): boolean {
    return !!this.expandedItems[item.label];
  }

  onMouseEnter(): void {
    this.isExpanded = true;
  }

  onMouseLeave(): void {
    this.isExpanded = false;
    this.expandedItems = {};
  }

}
